import { useEffect, useRef } from 'react';
import type { PageTheme } from '../model/pages';
import type { FormFieldDesign } from '../model/form-fields';
import { formBgCss, formAccentFor } from '../model/pages';
import type { StyleClasses } from './styles';
import CrmFormFrame, { type FormDone } from './CrmFormFrame';
import FormMock, { type PreviewForm } from './FormMock';
import type { PageFormLink } from './SectionRenderer';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The page's CRM form, or none. */
  form?: PageFormLink | null;
  pageId?: string;
  fieldDesign?: FormFieldDesign;
  /** No real form (galleries): a still of the template's form. */
  previewForm?: PreviewForm | null;
  theme: PageTheme;
  s: StyleClasses;
  /** The form opens in preview mode (preview=1, nothing saved). */
  formPreview?: boolean;
  /** A read-only render: the form takes no clicks. */
  preview?: boolean;
  onFormDone?: (done: FormDone) => void;
  onBooked?: () => void;
}

/** The page's form in a centered modal, used when theme.form_mode is 'popup'.
 *  Opens on a CTA click (see SectionRenderer). Closes on Esc, a scrim click or
 *  the close button, and locks the page's scroll while open. The panel uses the
 *  same card background (formBgCss) and border as the inline form section, so
 *  popup and inline look the same. Listens on the window the modal renders in
 *  (the editor canvas renders the page inside its own frame). */
export default function FormModal({ open, onClose, form, pageId, fieldDesign, previewForm, theme, s, formPreview, preview, onFormDone, onBooked }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // Esc to close + scroll lock on the page's own document while it shows (an
  // open modal with no form to show renders nothing, so it must not lock the page).
  const shows = open && (!!form || !!previewForm);
  useEffect(() => {
    if (!shows) return;
    const doc = ref.current?.ownerDocument ?? document;
    const win = doc.defaultView ?? window;
    const previousFocus = doc.activeElement as HTMLElement | null;
    closeButton.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current(); };
    win.addEventListener('keydown', onKey);
    const prev = doc.body.style.overflow;
    doc.body.style.overflow = 'hidden';
    return () => {
      win.removeEventListener('keydown', onKey);
      doc.body.style.overflow = prev;
      if (previousFocus?.isConnected && previousFocus !== doc.body) previousFocus.focus?.({ preventScroll: true });
    };
  }, [shows]);

  if (!shows) return null;
  const accent = formAccentFor(theme);
  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label={form?.name || 'Form'}
      className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={onClose}>
      <div className="relative my-auto w-full max-w-md overflow-hidden rounded-3xl border shadow-2xl"
        style={{ background: formBgCss(theme), borderColor: s.formBorder }}
        onMouseDown={(e) => e.stopPropagation()}>
        <button ref={closeButton} type="button" aria-label="Close"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-black/5 hover:text-slate-900"
          onClick={onClose}>✕</button>
        {form
          ? <CrmFormFrame slug={form.slug} name={form.name} pageId={pageId} accent={accent} fieldDesign={fieldDesign} preview={formPreview} blocked={preview}
              onDone={onFormDone} onBooked={onBooked} />
          : previewForm && <FormMock title={previewForm.title} description={previewForm.description} fields={previewForm.fields} accent={accent} fieldDesign={fieldDesign} />}
      </div>
    </div>
  );
}
